// EmailComposer — drawer-based composer for sending email from
// inside a record (household, contact, opportunity, transaction,
// rhythm) without leaving the page.
//
// Phase 9 of the Office 365 plan. Auto-applies the saved Outlook
// signature (phase 8b) on mount. Sends via POST /api/email/send.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClipboardList, FileText, Sparkles } from "lucide-react";

import { api, ApiError, type EmailMessageDetail } from "../../api";
import { useSession } from "../../session";
import {
  handleRichTextKeyDown,
  RichTextToolbar,
} from "../automations/RichTextToolbar";
import { Drawer } from "../Drawer";
import { useToast } from "../Toast";
import { Button, ErrorBanner } from "../../ui";
import {
  NotConnectedNotice,
  isNotConnectedError,
} from "../NotConnectedNotice";
import { AiHelpPanel } from "./AiHelpPanel";
import {
  ComposerAttachments,
  splitAttachments,
  type PendingAttachment,
} from "./ComposerAttachments";
import { FormLinkPicker } from "./FormLinkPicker";
import { RecipientAutocompleteField } from "./RecipientAutocompleteField";
import { ScheduleSendDialog } from "./ScheduleSendDialog";
import { bodyTemplateToHtml, TemplatePicker } from "./TemplatePicker";
import {
  type ComposerDraft,
  type RestoredRecipientSource,
  clearDraft,
  draftDiffersFromBaseline,
  draftKeyFor,
  loadDraft,
  resolveRestoredRecipients,
  saveDraft,
} from "../../utils/email-draft-autosave";

// Shape of an option in the From-picker. Mirrors what
// /api/email/shared-mailboxes returns plus a virtual "self" entry for
// the user's own mailbox (always the default).
interface FromOption {
  // null = signed-in user's own mailbox (no fromMailboxUpn passed
  // to the backend). Anything else is a registered shared mailbox.
  upn: string | null;
  label: string;
  description: string | null;
}

// The OS record the composer was opened from, when it was opened from
// one. Two things read it: the draft slot (a half-written note about
// one deal must never reappear on another — Nate Swartz, 2026-08-20)
// and the "This record" attachment source, which lists that record's
// own documents so the file the advisor wants is the first thing they
// see instead of something to go hunting for.
export interface EmailComposerRecordContext {
  entityType: "transaction" | "opportunity" | "service_record" | "household";
  entityId: string;
  // Plain-English name for the menu — "Duncan St", "Smith Family".
  label: string;
  // The record's own property address, when the caller already has one
  // in hand (a Processing/transaction record's `property_address`).
  // Distinct from `label`, which falls back to a non-address string
  // when there's no property yet — this is only ever a real address,
  // or absent. Used to lead the suggested subject line with the street
  // name, per Nate Swartz's 2026-08-27 ask.
  addressPrefix?: string | null;
}

// Which record types get their own draft slot. A household already has
// one (keyed by householdId); the work records are the ones that were
// sharing it.
const WORK_RECORD_TYPES = new Set(["transaction", "opportunity", "service_record"]);

export interface EmailComposerProps {
  open: boolean;
  onClose: () => void;
  // Record context — at least one is required.
  householdId?: string | null;
  contactId?: string | null;
  // The Processing / Opportunity / service record this compose belongs
  // to, when there is one.
  recordContext?: EmailComposerRecordContext | null;
  // Pre-fill values.
  prefillTo?: string[];
  prefillSubject?: string;
  prefillBodyHtml?: string;
  // Reply context — when present, In-Reply-To header is set so the
  // recipient's mail client groups the reply with the original.
  replyToMessageId?: string | null;
  // v1.x: Microsoft conversationId for the original message. When
  // set, AiHelpPanel pulls the thread context so the AI can read
  // prior messages when drafting a reply.
  replyToConversationId?: string | null;
  // For AI Help panel display in reply mode. When set, surfaces
  // the original subject + body preview to the model.
  replyToSubject?: string | null;
  replyToBodyPreview?: string | null;
  // Onsuccess callback so the host page can refresh the activity feed.
  onSent?: (activityId: string) => void;
}

interface SignatureState {
  html: string;
  loaded: boolean;
}

export function EmailComposer({
  open,
  onClose,
  householdId,
  contactId,
  recordContext,
  prefillTo,
  prefillSubject,
  prefillBodyHtml,
  replyToMessageId,
  replyToConversationId,
  replyToSubject,
  replyToBodyPreview,
  onSent,
}: EmailComposerProps) {
  const toast = useToast();
  const session = useSession();
  const editorRef = useRef<HTMLDivElement | null>(null);
  // Insert-a-form (Nate Swartz, 2026-08-28). The picker is a floating
  // panel, and clicking into it blurs the editor before the caret
  // position is needed — so the selection is snapshotted at the moment
  // the "Insert a form" button is pressed (same trick RichTextToolbar's
  // own insertLink uses for window.prompt) and restored just before the
  // link HTML goes in.
  const [formPickerOpen, setFormPickerOpen] = useState(false);
  const formPickerSavedRangeRef = useRef<Range | null>(null);
  const [to, setTo] = useState<string[]>(prefillTo ?? []);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState(prefillSubject ?? "");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notConnected, setNotConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [signature, setSignature] = useState<SignatureState>({
    html: "",
    loaded: false,
  });
  const [aiOpen, setAiOpen] = useState(false);
  // Files staged on this message (Nate, 2026-08-20). Deliberately NOT
  // part of the autosaved draft: a draft lives in localStorage and a
  // 3 MB base64 PDF has no business there, so a reopened draft brings
  // back the words and the advisor re-attaches. Cleared on open, on
  // send, and on save-to-drafts.
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  // Subject-line suggestion (Nate Swartz, 2026-08-27) — a dismissible
  // chip offered above the Subject field once there's enough to go on
  // (body text or an attachment), never shown once the writer has
  // typed a subject of their own. `subjectSuggestionDismissed` is the
  // "×" — it stops re-offering for the rest of this compose session,
  // separate from the chip simply being hidden because Subject is no
  // longer empty.
  const [subjectSuggestion, setSubjectSuggestion] = useState<string | null>(
    null,
  );
  const [subjectSuggestionDismissed, setSubjectSuggestionDismissed] =
    useState(false);
  const subjectSuggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Guards against a slow response landing after a newer request (or
  // after the writer already typed their own subject) from overwriting
  // what's on screen.
  const subjectSuggestRequestIdRef = useRef(0);
  const subjectRef = useRef(subject);
  useEffect(() => {
    subjectRef.current = subject;
  }, [subject]);
  // Polish #41 — template picker. Mounted next to the AI help
  // toggle so advisors can drop a saved template into the body
  // without leaving the composer.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  // v1.x phase 11.12 — "Save to Drafts" mode. Drafts the email in
  // the user's Outlook Drafts folder and shows a toast with an
  // "Open in Outlook" link. The advisor reviews + clicks Send in
  // Outlook themselves; OS never auto-sends.
  const [drafting, setDrafting] = useState(false);
  // From-picker (v1.x phase 11.8). Default null = signed-in user's
  // mailbox. selecting a shared mailbox plumbs `fromMailboxUpn`
  // through to /api/email/send.
  const [fromMailboxUpn, setFromMailboxUpn] = useState<string | null>(null);
  const [fromOptions, setFromOptions] = useState<FromOption[]>([]);
  // v1.x (2026-06-26, Joey Ellis feedback) — local draft autosave.
  // The composer persists in-progress drafts to localStorage so
  // closing it (Cancel / Esc / backdrop) never loses what was typed;
  // reopening for the same record/reply restores it. See
  // utils/email-draft-autosave.ts for the storage rationale.
  // A work record gets its own draft slot; everything else keys as it
  // always did.
  const workRecord =
    recordContext && WORK_RECORD_TYPES.has(recordContext.entityType)
      ? recordContext
      : null;
  const workRecordType = workRecord?.entityType ?? null;
  const workRecordId = workRecord?.entityId ?? null;
  const draftKey = useMemo(
    () =>
      draftKeyFor({
        ...(householdId !== undefined ? { householdId } : {}),
        ...(contactId !== undefined ? { contactId } : {}),
        ...(replyToMessageId !== undefined ? { replyToMessageId } : {}),
        recordEntityType: workRecordType,
        recordEntityId: workRecordId,
      }),
    [householdId, contactId, replyToMessageId, workRecordType, workRecordId],
  );
  // The empty starting point (prefill + signature) the composer opens
  // with. Autosave compares live state against this so an untouched
  // seed never counts as a draft. Set in the seed-editor effect.
  const baselineRef = useRef<Omit<ComposerDraft, "savedAt"> | null>(null);
  // The draft (if any) loaded for the current open session — read by
  // the seed-editor effect so it restores the saved body instead of
  // the bare prefill.
  const restoredDraftRef = useRef<ComposerDraft | null>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // null = no draft was restored on this open. Otherwise, what happened
  // to the recipient line, which is the part the user has to be told
  // about (see resolveRestoredRecipients).
  const [draftRestored, setDraftRestored] =
    useState<RestoredRecipientSource | null>(null);

  // Reset to/cc/bcc/subject whenever the drawer opens — defensive
  // for re-mounts. fromMailboxUpn intentionally NOT reset so a user
  // who just sent from insurance@ keeps that selection on the next
  // compose. Picker still defaults to "self" on first open. When a
  // saved draft exists for this composer context, restore it over the
  // prefill.
  useEffect(() => {
    if (open) {
      const saved = loadDraft(draftKey);
      restoredDraftRef.current = saved;
      // Recipients are resolved separately from the rest of the draft:
      // an audience the caller just named (three checked parties, a
      // "email this person" link) beats one a stale draft remembers.
      const recipients = resolveRestoredRecipients(prefillTo, saved);
      setTo(recipients.to);
      if (saved) {
        setSubject(saved.subject);
        setCc(saved.cc);
        setBcc(saved.bcc);
        setShowCcBcc(saved.cc.length > 0 || saved.bcc.length > 0);
        setDraftRestored(recipients.source);
      } else {
        setSubject(prefillSubject ?? "");
        setCc([]);
        setBcc([]);
        setShowCcBcc(false);
        setDraftRestored(null);
      }
      setError(null);
      // Stale-state audit fix (2026-05-29) — the sidebar composer is
      // mounted for the whole session, so these toggle flags survived
      // close→reopen (e.g. AI-help panel re-opening expanded on a
      // fresh compose). Reset the sub-panel toggles on open too.
      setAiOpen(false);
      setTemplatePickerOpen(false);
      setScheduleOpen(false);
      setAttachments([]);
      setSubjectSuggestion(null);
      setSubjectSuggestionDismissed(false);
    }
  }, [open, prefillTo, prefillSubject, draftKey]);

  // Build the From-picker options once per open. Self entry first,
  // shared mailboxes from the registry next. Failure is non-fatal —
  // composer still works for the user's own mailbox.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const userEmail = session.user?.email ?? "you";
      const baseOptions: FromOption[] = [
        { upn: null, label: userEmail, description: "Your mailbox" },
      ];
      try {
        const resp = await api.getSharedMailboxes();
        if (cancelled) return;
        // S26 — a mailbox that's visible for reading (Ryan's, Jarred's)
        // is not necessarily one you can send AS. Only sendable entries
        // belong in the compose From-picker; offering one that always
        // 400s on Send is a dead end, not a choice.
        const shared = resp.mailboxes
          .filter((m) => m.sendable)
          .map((m) => ({
            upn: m.upn,
            label: `${m.display_name} — ${m.upn}`,
            description: m.description,
          }));
        setFromOptions([...baseOptions, ...shared]);
      } catch {
        if (!cancelled) setFromOptions(baseOptions);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, session.user?.email]);

  // Load signature once per session.
  useEffect(() => {
    if (!open || signature.loaded) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await api.getEmailSignature();
        if (!cancelled) {
          setSignature({ html: resp.signature_html ?? "", loaded: true });
        }
      } catch {
        // Signature is optional; ignore failures (the composer still
        // works, just without a signature).
        if (!cancelled) setSignature({ html: "", loaded: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, signature.loaded]);

  // When the drawer + signature are loaded, seed the editor with
  // prefillBodyHtml + signature. The leading <p><br></p> gives the
  // user an empty paragraph to start typing in (above the signature
  // block); the signature itself goes in its own <div> for visual
  // grouping. No "--" separator — Outlook doesn't use it.
  useEffect(() => {
    if (!open || !signature.loaded || !editorRef.current) return;
    const initialBody = prefillBodyHtml ?? "";
    const sig = signature.html
      ? `<div class="composer-signature">${signature.html}</div>`
      : "";
    const seededHtml = `${initialBody}<p><br></p>${sig}`;
    // Baseline = the empty starting point. Autosave detects real edits
    // by comparing live state against this.
    baselineRef.current = {
      to: prefillTo ?? [],
      cc: [],
      bcc: [],
      subject: prefillSubject ?? "",
      bodyHtml: seededHtml,
      // Stamped onto every save below so a later open can tell whether
      // this draft was written for the audience being asked for then.
      openedWithTo: prefillTo ?? [],
    };
    const saved = restoredDraftRef.current;
    editorRef.current.innerHTML = saved ? saved.bodyHtml : seededHtml;
  }, [
    open,
    signature.loaded,
    signature.html,
    prefillBodyHtml,
    prefillTo,
    prefillSubject,
  ]);

  // Persist (or clear) the live draft for this composer context.
  // Saves only when the state has meaningful edits over the seeded
  // baseline; clears otherwise, so closing an untouched composer
  // leaves no stale draft behind.
  const persistDraftNow = useCallback(() => {
    if (!open || !editorRef.current || !baselineRef.current) return;
    const current = {
      to,
      cc,
      bcc,
      subject,
      bodyHtml: editorRef.current.innerHTML,
      // The audience this draft was started for — NOT the live `to`,
      // which the user may have edited by hand and which must survive
      // a reopen from the same place.
      openedWithTo: baselineRef.current.openedWithTo ?? [],
    };
    if (draftDiffersFromBaseline(current, baselineRef.current)) {
      saveDraft(draftKey, current);
    } else {
      clearDraft(draftKey);
    }
  }, [open, to, cc, bcc, subject, draftKey]);

  // Debounced autosave. Fires ~700ms after the last edit.
  const scheduleAutosave = useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      persistDraftNow();
    }, 700);
  }, [persistDraftNow]);

  // Recipient / subject changes trigger an autosave. Body changes are
  // handled by the editor's onInput below (contentEditable doesn't
  // surface its content through React state).
  useEffect(() => {
    if (!open) return;
    scheduleAutosave();
  }, [open, to, cc, bcc, subject, scheduleAutosave]);

  // Ask the AI for a subject suggestion once there's enough on the
  // page to go on (body text or an attachment) — never while the
  // writer already has a subject of their own, never once they've
  // dismissed one this session. A failed or empty response is silent:
  // the composer just doesn't offer a suggestion, same as if nothing
  // had been asked.
  const requestSubjectSuggestion = useCallback(() => {
    if (!open || subjectSuggestionDismissed) return;
    if (subject.trim().length > 0) return;
    const bodyText = editorRef.current?.innerText?.trim() ?? "";
    const attachmentNames = attachments.map((a) => a.name);
    if (bodyText.length < 8 && attachmentNames.length === 0) return;
    const requestId = ++subjectSuggestRequestIdRef.current;
    api
      .suggestEmailSubject({
        body_text: bodyText,
        attachment_names: attachmentNames,
        address_prefix: recordContext?.addressPrefix ?? null,
      })
      .then((resp) => {
        if (requestId !== subjectSuggestRequestIdRef.current) return; // superseded
        if (subjectRef.current.trim().length > 0) return; // typed while waiting
        if (resp.suggestion) setSubjectSuggestion(resp.suggestion);
      })
      .catch(() => {
        // Best-effort — nothing to show the writer for a failed guess.
      });
  }, [open, subject, subjectSuggestionDismissed, attachments, recordContext]);

  const scheduleSubjectSuggest = useCallback(() => {
    if (subjectSuggestTimerRef.current) {
      clearTimeout(subjectSuggestTimerRef.current);
    }
    subjectSuggestTimerRef.current = setTimeout(() => {
      requestSubjectSuggestion();
    }, 900);
  }, [requestSubjectSuggestion]);

  // Attaching or removing a file is itself "more info added to the
  // email" (Nate's phrasing) — re-offer without waiting for a keystroke.
  useEffect(() => {
    if (!open) return;
    scheduleSubjectSuggest();
  }, [open, attachments, scheduleSubjectSuggest]);

  useEffect(() => {
    return () => {
      if (subjectSuggestTimerRef.current) {
        clearTimeout(subjectSuggestTimerRef.current);
      }
    };
  }, []);

  // Clear any pending autosave timer on unmount.
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, []);

  // Snapshot the caret before the form picker opens (see the ref
  // comment above) and close the Template picker so the two floating
  // panels, which share screen position, don't stack.
  const openFormPicker = useCallback(() => {
    const el = editorRef.current;
    const sel = document.getSelection();
    formPickerSavedRangeRef.current =
      sel && sel.rangeCount > 0 && el?.contains(sel.anchorNode)
        ? sel.getRangeAt(0).cloneRange()
        : null;
    setTemplatePickerOpen(false);
    setFormPickerOpen(true);
  }, []);

  // Restore the snapshotted caret (or fall back to the end of the
  // body) and insert the form-link HTML there.
  const insertFormLink = useCallback(
    (html: string) => {
      const el = editorRef.current;
      if (el) {
        el.focus();
        const sel = document.getSelection();
        const range = formPickerSavedRangeRef.current ?? document.createRange();
        if (!formPickerSavedRangeRef.current) {
          range.selectNodeContents(el);
          range.collapse(false);
        }
        sel?.removeAllRanges();
        sel?.addRange(range);
        document.execCommand("insertHTML", false, html);
        scheduleAutosave();
        scheduleSubjectSuggest();
      }
      setFormPickerOpen(false);
    },
    [scheduleAutosave, scheduleSubjectSuggest],
  );

  // Wrap the parent onClose so the latest draft is flushed
  // synchronously (the most recent keystrokes may still be inside the
  // debounce window). Closing stays inside the OS — the Drawer simply
  // dismisses; nothing navigates the advisor out to their mailbox.
  const handleClose = useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    persistDraftNow();
    onClose();
  }, [persistDraftNow, onClose]);

  // Cancel any pending autosave and drop the persisted draft. Called
  // when the work is committed elsewhere (sent, scheduled, or saved to
  // the Outlook Drafts folder) so a stale debounce timer can't re-save
  // it after the fact.
  const discardLocalDraft = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    clearDraft(draftKey);
  }, [draftKey]);

  const send = useCallback(async () => {
    if (!editorRef.current) return;
    if (to.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    if (subject.trim().length === 0) {
      setError("Subject is required.");
      return;
    }
    // Phase 3: backend now derives household/contact context from
    // recipients when neither is supplied. The composer no longer
    // requires explicit context — opening from a record passes
    // householdId/contactId; opening with no context (e.g. a "New
    // email" button outside a record) sends without those fields.
    setSending(true);
    setError(null);
    try {
      const files = splitAttachments(attachments);
      const result = await api.sendEmail({
        to,
        ...(cc.length > 0 ? { cc } : {}),
        ...(bcc.length > 0 ? { bcc } : {}),
        subject: subject.trim(),
        bodyHtml: editorRef.current.innerHTML,
        ...(files.attachments.length > 0
          ? { attachments: files.attachments }
          : {}),
        ...(files.attachmentRefs.length > 0
          ? { attachmentRefs: files.attachmentRefs }
          : {}),
        ...(householdId ? { householdId } : {}),
        ...(contactId ? { contactId } : {}),
        // S14 — tell the server which record this was composed from, so
        // "we emailed this quote and nobody replied" becomes answerable.
        // workRecord is already the filtered work-record context this
        // component computes for its draft slot; until now it never left
        // the browser. `household` is deliberately absent from that set:
        // the activity already lands at household grain.
        ...(workRecordType && workRecordId
          ? {
              recordEntityType: workRecordType as
                | "service_record"
                | "opportunity"
                | "transaction",
              recordEntityId: workRecordId,
            }
          : {}),
        ...(replyToMessageId ? { inReplyToMessageId: replyToMessageId } : {}),
        ...(fromMailboxUpn ? { fromMailboxUpn } : {}),
      });
      toast.success("Email sent.");
      discardLocalDraft();
      if (onSent && result.activity_id) onSent(result.activity_id);
      onClose();
    } catch (err) {
      if (isNotConnectedError(err)) {
        // Not a failure — the mailbox was never connected. Show the
        // connect path instead of an error (2026-06-09 audit, 2.1).
        setNotConnected(true);
        setError(null);
      } else {
        const message =
          err instanceof ApiError ? err.message : "Failed to send email.";
        setError(message);
        toast.error(message);
      }
    } finally {
      setSending(false);
    }
  }, [
    to,
    cc,
    bcc,
    subject,
    attachments,
    householdId,
    contactId,
    replyToMessageId,
    fromMailboxUpn,
    workRecordType,
    workRecordId,
    onClose,
    onSent,
    toast,
    discardLocalDraft,
  ]);

  // v1.x scheduled send. Same validation as `send` minus the "send
  // now" specifics. Reply-thread continuity is dropped here on
  // purpose — Microsoft's draft-create endpoint doesn't accept the
  // `internetMessageHeaders` parameter the same way /me/sendMail
  // does, so threaded scheduled-replies need a richer flow we'll
  // build only if anyone asks. Plain "scheduled compose" works.
  const sendScheduled = useCallback(
    async (sendAtIso: string) => {
      if (!editorRef.current) return;
      if (to.length === 0) {
        setError("Add at least one recipient.");
        setScheduleOpen(false);
        return;
      }
      if (subject.trim().length === 0) {
        setError("Subject is required.");
        setScheduleOpen(false);
        return;
      }
      setScheduling(true);
      setError(null);
      try {
        const result = await api.sendScheduledEmail({
          to,
          ...(cc.length > 0 ? { cc } : {}),
          ...(bcc.length > 0 ? { bcc } : {}),
          subject: subject.trim(),
          bodyHtml: editorRef.current.innerHTML,
          ...(householdId ? { householdId } : {}),
          ...(contactId ? { contactId } : {}),
          sendAtIso,
          ...(fromMailboxUpn ? { fromMailboxUpn } : {}),
        });
        const when = new Date(sendAtIso).toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        toast.success(`Scheduled to send ${when}.`);
        discardLocalDraft();
        if (onSent && result.activity_id) onSent(result.activity_id);
        setScheduleOpen(false);
        onClose();
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : "Failed to schedule send.";
        setError(message);
        toast.error(message);
      } finally {
        setScheduling(false);
      }
    },
    [
      to,
      cc,
      bcc,
      subject,
      householdId,
      contactId,
      fromMailboxUpn,
      onClose,
      onSent,
      toast,
      discardLocalDraft,
    ],
  );

  const saveToDrafts = useCallback(async () => {
    if (!editorRef.current) return;
    if (to.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    if (subject.trim().length === 0) {
      setError("Subject is required.");
      return;
    }
    setDrafting(true);
    setError(null);
    try {
      const files = splitAttachments(attachments);
      const result = await api.saveEmailDraft({
        to,
        ...(cc.length > 0 ? { cc } : {}),
        ...(bcc.length > 0 ? { bcc } : {}),
        subject: subject.trim(),
        bodyHtml: editorRef.current.innerHTML,
        ...(files.attachments.length > 0
          ? { attachments: files.attachments }
          : {}),
        ...(files.attachmentRefs.length > 0
          ? { attachmentRefs: files.attachmentRefs }
          : {}),
        ...(fromMailboxUpn ? { fromMailboxUpn } : {}),
      });
      // Stay in the OS by default (Joey Ellis feedback, 2026-06-26 —
      // saving a draft used to force-open Outlook in a new tab, which
      // read as "the OS dumped me into my mailbox"). The draft is now
      // safely in the advisor's Outlook Drafts folder; offer an
      // optional, user-initiated "Open in Outlook" toast action
      // instead of auto-navigating away. The local autosave draft is
      // redundant once it's in Outlook, so drop it.
      if (result.web_link) {
        const link = result.web_link;
        toast.success("Saved to your Outlook Drafts folder.", {
          action: {
            label: "Open in Outlook",
            onClick: () => {
              try {
                window.open(link, "_blank", "noopener,noreferrer");
              } catch {
                // Popup blockers can block this — harmless.
              }
            },
          },
        });
      } else {
        toast.success("Saved to your Outlook Drafts. Open Outlook to review + send.");
      }
      discardLocalDraft();
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Failed to save draft.";
      setError(message);
      toast.error(message);
    } finally {
      setDrafting(false);
    }
  }, [
    to,
    cc,
    bcc,
    subject,
    attachments,
    fromMailboxUpn,
    onClose,
    toast,
    discardLocalDraft,
  ]);

  // A big attachment uploads while the advisor writes (see
  // ComposerAttachments). Send has to wait for it: the message would
  // otherwise leave carrying a reference to a file that hasn't finished
  // arriving, which is the "sent it without the contract" failure in a
  // new costume. Disabled with a reason, never silently.
  const uploadInFlight = attachments.some((a) => a.uploading);

  const footer = useMemo(
    () => (
      <>
        <Button
          variant="ghost"
          onClick={handleClose}
          disabled={sending || scheduling || drafting}
        >
          Cancel
        </Button>
        <Button
          variant="ghost"
          onClick={() => setScheduleOpen(true)}
          disabled={sending || scheduling || drafting || attachments.length > 0}
          title={
            attachments.length > 0
              ? "Scheduled sends can't carry attachments yet — send now, or save to Drafts and send from Outlook."
              : undefined
          }
        >
          Schedule send…
        </Button>
        <Button
          variant="ghost"
          onClick={() => void saveToDrafts()}
          disabled={sending || scheduling || drafting || uploadInFlight}
          title={
            uploadInFlight
              ? "One of your attachments is still uploading."
              : "Save to your Outlook Drafts folder. You'll review and send from Outlook — Stewardship OS won't auto-send."
          }
        >
          {drafting ? "Saving…" : "Save to Drafts"}
        </Button>
        <Button
          variant="primary"
          onClick={() => void send()}
          disabled={sending || scheduling || drafting || uploadInFlight}
          title={
            uploadInFlight
              ? "One of your attachments is still uploading."
              : undefined
          }
        >
          {sending ? "Sending…" : uploadInFlight ? "Uploading…" : "Send"}
        </Button>
      </>
    ),
    [
      handleClose,
      send,
      saveToDrafts,
      sending,
      scheduling,
      drafting,
      attachments.length,
      uploadInFlight,
    ],
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={replyToMessageId ? "Reply" : "New email"}
      width={560}
      footer={footer}
    >
      {notConnected ? <NotConnectedNotice context="send" compact /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      {draftRestored === "prefill_replaces_draft" ? (
        // The draft was written for a different audience than the one
        // just picked. Say so out loud: silently swapping recipients is
        // the failure this banner exists to make impossible.
        <div className="composer-draft-note" role="status">
          Draft restored — we kept what you'd written, and addressed it to
          the {to.length === 1 ? "person" : `${to.length} people`} you just
          selected.
        </div>
      ) : draftRestored ? (
        <div className="composer-draft-note" role="status">
          Draft restored — we kept what you'd written. It autosaves as you
          type.
        </div>
      ) : null}
      {/* From-picker (v1.x phase 11.8). Hidden when only the user's
          own mailbox is available (registry returned no shared
          mailboxes). Otherwise renders a select listing the user's
          mailbox plus each registered shared mailbox. */}
      {fromOptions.length > 1 ? (
        <div className="composer-field">
          <label htmlFor="composer-from">From</label>
          <select
            id="composer-from"
            value={fromMailboxUpn ?? ""}
            onChange={(e) =>
              setFromMailboxUpn(e.target.value === "" ? null : e.target.value)
            }
            disabled={sending || scheduling}
            className="composer-from-select"
          >
            {fromOptions.map((opt) => (
              <option key={opt.upn ?? "self"} value={opt.upn ?? ""}>
                {opt.label}
              </option>
            ))}
          </select>
          {fromMailboxUpn ? (
            <small className="composer-from-hint">
              Sending from a shared mailbox. The recipient sees the message as
              from <code>{fromMailboxUpn}</code>; reply will land in that
              mailbox, not yours.
            </small>
          ) : null}
        </div>
      ) : null}
      <RecipientAutocompleteField
        label="To"
        value={to}
        onChange={setTo}
        disabled={sending}
        placeholder="name@example.com"
      />
      {!showCcBcc ? (
        <button
          type="button"
          className="composer-cc-toggle"
          onClick={() => setShowCcBcc(true)}
        >
          + Cc / Bcc
        </button>
      ) : (
        <>
          <RecipientAutocompleteField
            label="Cc"
            value={cc}
            onChange={setCc}
            disabled={sending}
          />
          <RecipientAutocompleteField
            label="Bcc"
            value={bcc}
            onChange={setBcc}
            disabled={sending}
          />
        </>
      )}
      <div className="composer-field">
        <label>Subject</label>
        {subjectSuggestion ? (
          <div className="composer-subject-suggestion">
            <button
              type="button"
              className="composer-subject-suggestion-accept"
              onClick={() => {
                setSubject(subjectSuggestion);
                setSubjectSuggestion(null);
              }}
            >
              <Sparkles size={13} strokeWidth={1.5} aria-hidden />
              <span className="composer-subject-suggestion-text">
                {subjectSuggestion}
              </span>
            </button>
            <button
              type="button"
              className="composer-subject-suggestion-dismiss"
              aria-label="Dismiss suggested subject"
              onClick={() => {
                setSubjectSuggestion(null);
                setSubjectSuggestionDismissed(true);
              }}
            >
              ×
            </button>
          </div>
        ) : null}
        <input
          type="text"
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            if (subjectSuggestion) setSubjectSuggestion(null);
          }}
        />
      </div>
      <div className="composer-field">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <label>Body</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              className="composer-cc-toggle"
              onClick={() => {
                setFormPickerOpen(false);
                setTemplatePickerOpen(true);
              }}
              aria-label="Insert a saved template"
            >
              <ClipboardList size={14} strokeWidth={1.5} aria-hidden /> Template
            </button>
            <button
              type="button"
              className="composer-cc-toggle"
              onClick={openFormPicker}
              aria-label="Insert a form"
            >
              <FileText size={14} strokeWidth={1.5} aria-hidden /> Insert form
            </button>
            <button
              type="button"
              className="composer-cc-toggle"
              onClick={() => setAiOpen((v) => !v)}
            >
              {aiOpen ? (
                "Hide AI help"
              ) : (
                <>
                  <Sparkles size={14} strokeWidth={1.5} aria-hidden /> AI help
                </>
              )}
            </button>
          </div>
        </div>
        <div className="rt-editor composer-body-editor">
          <RichTextToolbar
            editorRef={editorRef}
            onChange={() => {
              scheduleAutosave();
              scheduleSubjectSuggest();
            }}
          />
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="composer-body"
            aria-label="Email body"
            onInput={() => {
              scheduleAutosave();
              scheduleSubjectSuggest();
            }}
            onKeyDown={(e) =>
              handleRichTextKeyDown(e, () => {
                scheduleAutosave();
                scheduleSubjectSuggest();
              })
            }
          />
        </div>
      </div>
      {/* Attachments sit below the body, where Outlook puts them and
          where the advisor looks for them before hitting Send. */}
      <ComposerAttachments
        value={attachments}
        onChange={setAttachments}
        recordContext={
          recordContext ??
          // No work record: still offer the household's own documents,
          // so a compose from a household screen isn't the one place
          // you can't attach what's already on file.
          (householdId
            ? {
                entityType: "household" as const,
                entityId: householdId,
                label: "this household",
              }
            : null)
        }
        disabled={sending || scheduling || drafting}
      />
      {aiOpen ? (
        // v1.x phase 11.11: pass additional recipients (to[1..] +
        // cc) so the AI can address everyone by name. We only know
        // the email at the composer level — the server doesn't
        // currently resolve names + households for these, so the
        // model gets emails alone. Future enhancement: server-side
        // resolve via findContactsByEmails.
        <AiHelpPanel
          draft={editorRef.current?.innerHTML ?? ""}
          recipientEmail={to[0] ?? null}
          {...(() => {
            const extras: Array<{ email: string }> = [];
            for (let i = 1; i < to.length; i++) {
              const email = to[i];
              if (email) extras.push({ email });
            }
            for (const email of cc) {
              if (email) extras.push({ email });
            }
            return extras.length > 0
              ? { additionalRecipients: extras }
              : {};
          })()}
          {...(replyToConversationId
            ? { conversationId: replyToConversationId }
            : {})}
          {...(replyToSubject ? { inReplyToSubject: replyToSubject } : {})}
          {...(replyToBodyPreview
            ? { inReplyToBodyPreview: replyToBodyPreview }
            : {})}
          onAccept={(suggestion) => {
            // Replace the editor body with the suggestion + signature.
            if (editorRef.current) {
              const sig = signature.html
                ? `<div class="composer-signature">${signature.html}</div>`
                : "";
              // Convert plain-text suggestion to HTML paragraphs.
              const asHtml = suggestion
                .split("\n\n")
                .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
                .join("");
              editorRef.current.innerHTML = `${asHtml}<p><br></p>${sig}`;
            }
            setAiOpen(false);
          }}
          onClose={() => setAiOpen(false)}
        />
      ) : null}
      <ScheduleSendDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onConfirm={(sendAtIso) => void sendScheduled(sendAtIso)}
        busy={scheduling}
      />
      <TemplatePicker
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onPick={(t) => {
          // Polish #41 — replace subject + body with the template's
          // content. Merge tokens stay literal so the advisor can
          // confirm each substitution by hand (per the "no auto-blast
          // from the 1:1 composer" rule).
          setSubject(t.subject_template);
          if (editorRef.current) {
            const sig = signature.html
              ? `<div class="composer-signature">${signature.html}</div>`
              : "";
            editorRef.current.innerHTML = `${bodyTemplateToHtml(
              t.body_template,
            )}<p><br></p>${sig}`;
          }
          setError(null);
        }}
      />
      <FormLinkPicker
        open={formPickerOpen}
        onClose={() => setFormPickerOpen(false)}
        householdId={householdId ?? null}
        onInsert={insertFormLink}
      />
    </Drawer>
  );
}

// Helper to derive a Reply prefill from a message detail. Returns
// the full set of EmailComposer props needed to open in reply mode,
// including the v1.x AI thread-context fields.
export function buildReplyPrefill(message: EmailMessageDetail): {
  prefillTo: string[];
  prefillSubject: string;
  prefillBodyHtml: string;
  replyToMessageId: string;
  replyToConversationId: string | null;
  replyToSubject: string;
  replyToBodyPreview: string;
} {
  const subject = message.subject.toLowerCase().startsWith("re:")
    ? message.subject
    : `Re: ${message.subject}`;
  const headerLine = `<p style="color:#888;font-size:12px">On ${new Date(
    message.receivedDateTime,
  ).toLocaleString()}, ${message.fromName ?? message.fromAddress ?? "(unknown)"} wrote:</p>`;
  const quoted = `<blockquote style="border-left:2px solid #ddd;padding-left:12px;color:#555">${
    message.body.contentType === "html" ? message.body.content : ""
  }</blockquote>`;
  // Body preview for the AI: strip HTML, cap at ~600 chars. Same
  // bound as the server-side cap on threadMessages[].bodyExcerpt.
  const bodyPreviewSource =
    message.body.contentType === "html"
      ? message.body.content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
      : message.body.content;
  return {
    prefillTo: message.fromAddress ? [message.fromAddress] : [],
    prefillSubject: subject,
    prefillBodyHtml: `<p></p>${headerLine}${quoted}`,
    replyToMessageId: message.internetMessageId ?? message.id,
    replyToConversationId: message.conversationId ?? null,
    replyToSubject: message.subject,
    replyToBodyPreview: bodyPreviewSource.slice(0, 600),
  };
}
